/**
 * onboardMeeting.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import React from "react";

import { Alert, Button, FormControl, TextField } from "@mui/material";

import { AuthContext } from "./auth.context";
import { LoginModal } from "./login.component";

import { Brand } from "../models/brand";
import { Meeting } from "../models/meeting";
import { authServiceFactory } from "../services/auth.service";

interface Props {
  accountID: string,
  meeting: Meeting,
  didLoginUser(): Promise<void>,
  didCreateTempUser(): Promise<void>
}

export function OnboardMeetingComponent(props: Props): React.ReactElement<any, any> {
  const authContext = React.useContext(AuthContext);
  const [errMsg, setErrMsg] = React.useState<string>("");
  const [openLoginModal, setOpenLoginModal] = React.useState<boolean>(false);
  const [firstname, setFirstName] = React.useState<string>("");
  const [lastname, setLastName] = React.useState<string>("");
  const [firstNameErr, setFirstNameErr] = React.useState<string>("");
  const [lastNameErr, setLastNameErr] = React.useState<string>("");
  const [saving, setSaving] = React.useState<boolean>(false);
  const lnameRef = React.useRef<HTMLInputElement | null>(null);

  const productName = authContext.brand === Brand.WORKSPACE ? "Mercury Workspace" : "GLYPH";

  function updateValue(event: React.SyntheticEvent): void {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = inputElem.value;
    switch (propName) {
      case "firstname": setFirstName(newValue); break;
      case "lastname": setLastName(newValue); break;
    }
  }

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    switch (propName) {
      case "firstname":
        if (!firstname) {
          setFirstNameErr("Enter a first name.");
        }
        break;
      case "lastname":
        if (!lastname) {
          setLastNameErr("Enter a last name.");
        }
        break;
      default:
        console.warn("Unknown input:", inputElem);
        return;
    }
  }

  function onKeyUp(event: React.KeyboardEvent) {
    if (event.key !== "Enter") { return; }
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    switch (propName) {
      case "firstname":
        lnameRef.current?.focus();
        break;
      case "lastname":
        doTempAccountCreation();
        break;
      default:
        return;
    }
  }

  async function doTempAccountCreation(): Promise<void> {
    try {
      if (saving) { return; }
      setSaving(true);

      await authServiceFactory().createTempUser(
          props.accountID, props.meeting._id,
          props.meeting.token, firstname, lastname);

      await props.didCreateTempUser();
    } catch (err) {
      console.error("Error creating membership:", err);
      setErrMsg((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onOtherUserLogin() {
    setOpenLoginModal(false);
    await props.didLoginUser();
  }

  return <div className="main-section-inner onboarding-welcome-container">
     <header className="iris-header welcome-heading">
        <div className="logo-img" />
      </header>
    <h1 className="welcome-heading">Welcome To {productName}</h1>
    { errMsg && <Alert
        variant="filled"
        severity="error"
        onClick={() => setErrMsg("")}>{errMsg}</Alert> }

    <div className="meeting-welcome-container">
      <header>
        We just need a little information to get you into your meeting.
      </header>
      <section>
        <form className="form-wrapper onboarding-meeting-form">
          <div className="form-row">
            <FormControl fullWidth>
              <TextField id="fname-input" label="First Name" name="firstname" required
                type="text" size="small"
                autoComplete="given-name"
                error={!!firstNameErr}
                helperText={firstNameErr}
                variant="outlined"
                value={firstname}
                onChange={updateValue}
                onBlur={onBlur}
                onKeyPress={onKeyUp} />
            </FormControl>
          </div>
          <div className="form-row">
            <FormControl fullWidth>
              <TextField id="lastname-input" label="Last Name" type="text" name="lastname"
                autoComplete="family-name" size="small"
                required
                error={!!lastNameErr}
                helperText={lastNameErr}
                variant="outlined"
                value={lastname}
                inputRef={lnameRef}
                onChange={updateValue}
                onBlur={onBlur}
                onKeyPress={onKeyUp} />
            </FormControl>
          </div>
          <div className="button-row">
            <Button color="primary" variant="contained"
                disableRipple disableTouchRipple
                onClick={doTempAccountCreation}>
              Continue
            </Button>
          </div>
          <div className="login-option">
            <div><span>Have an account?</span></div>
            <div>
              <Button
                disableRipple disableTouchRipple
                onClick={() => setOpenLoginModal(true)}>Log in</Button>
            </div>
          </div>
        </form>
      </section>
    </div>

    <LoginModal open={openLoginModal}
        onFinish={onOtherUserLogin}
        onClose={() => setOpenLoginModal(false) } />
  </div>;
}
